import { useState } from "react";
import { sanitizeReviewText } from "@/lib/sanitize-html";
import IconStarFill from "@/components/icons/icon-star-fill";
import IconStarLine from "@/components/icons/icon-star-line";
import IconRecycleBinThrow from "@/components/icons/icon-recycle-bin-throw";
import AudioPlayer from "@/components/ui/audio-player";
import VideoModal from "./video-modal";
import { getTagColorStyles } from "@/modules/tag/colors";
import type { ReviewContentType, ReviewTag, VideoStatus } from "../types";

export interface ReviewMasonryItemProps {
	// Testimonial
	rating: number;
	type: string;
	testimonial: string;
	date: string;
	source?: string;
	tags?: ReviewTag[];
	status?: "draft" | "pending" | "approved" | "unapproved";
	// Video
	muxPlaybackId?: string | null;
	videoThumbnailUrl?: string | null;
	videoDuration?: number | null;
	videoStatus?: VideoStatus | null;
	isVideoProcessing?: boolean;
	// Audio
	contentType?: ReviewContentType | null;
	audioUrl?: string | null;
	audioDuration?: number | null;
	// Person
	customerName: string;
	customerHeadline?: string;
	customerJobTitle?: string;
	customerAvatarUrl?: string | null;
	// Company
	companyName?: string;
	companyLogoUrl?: string | null;
	// Actions
	onDelete?: () => void;
	onEdit?: () => void;
	onApprove?: () => void;
	onUnapprove?: () => void;
	onClick?: () => void;
	isDemoMode?: boolean;
	// Loading state
	isStatusChanging?: boolean;
}

function StatusBadge({ status }: { status?: "draft" | "pending" | "approved" | "unapproved" }) {
	if (!status) return null;

	const config = {
		draft: { label: "Draft", className: "bg-gray-100 text-gray-600" },
		pending: { label: "Pending", className: "bg-yellow-100 text-yellow-700" },
		approved: { label: "Approved", className: "bg-green-100 text-green-700" },
		unapproved: { label: "Unapproved", className: "bg-red-100 text-red-700" },
	};

	const { label, className } = config[status];

	return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${className}`}>{label}</span>;
}

function StarRating({ rating }: { rating: number }) {
	return (
		<div className="flex items-center gap-0.5">
			{[1, 2, 3, 4, 5].map((star) => (star <= rating ? <IconStarFill key={star} className="w-5 h-5 text-brand" /> : <IconStarLine key={star} className="w-5 h-5 text-gray-200" />))}
		</div>
	);
}

function ClassificationBadge({ type }: { type: string }) {
	const isTestimonial = type === "testimonial";
	return (
		<span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${isTestimonial ? "bg-green-100 text-green-700" : "bg-blue-50 text-blue-700"}`}>
			{isTestimonial ? "Testimonial" : "Feedback"}
		</span>
	);
}

function SourceBadge({ source }: { source: string }) {
	const getSourceInfo = (source: string) => {
		const sources: Record<string, { label: string; key: string }> = {
			manual: { label: "Manual", key: "manual" },
			form: { label: "Form", key: "form" },
			import: { label: "Import", key: "import" },
			twitter: { label: "Twitter", key: "twitter" },
			"x.com": { label: "Twitter", key: "twitter" },
			linkedin: { label: "LinkedIn", key: "linkedin" },
			facebook: { label: "Facebook", key: "facebook" },
			instagram: { label: "Instagram", key: "instagram" },
			google_reviews: { label: "Google", key: "google" },
			capterra: { label: "Capterra", key: "capterra" },
			trustpilot: { label: "Trustpilot", key: "trustpilot" },
			slack: { label: "Slack", key: "slack" },
			"product-hunt": { label: "Product Hunt", key: "product-hunt" },
			email: { label: "Email", key: "email" },
			other: { label: "Other", key: "fallback" },
		};
		const normalized = source.toLowerCase();
		const match = sources[normalized] || Object.entries(sources).find(([k]) => normalized.includes(k))?.[1];
		const key = match?.key || "fallback";
		const label = match?.label || source;
		return { label, icon: <img src={`/images/review-source-icons/source-${key}.svg`} alt={label} className="w-3 h-3" /> };
	};

	const { label, icon } = getSourceInfo(source);

	return (
		<span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-xs text-tertiary">
			<span>{icon}</span>
			<span>{label}</span>
		</span>
	);
}

function formatDuration(seconds: number): string {
	const mins = Math.floor(seconds / 60);
	const secs = Math.floor(seconds % 60);
	return `${mins}:${secs.toString().padStart(2, "0")}`;
}

function CustomerHeadline({ headline, companyName, jobTitle }: { headline: string | undefined; companyName: string | undefined; jobTitle: string | undefined }) {
	if (headline) {
		return <div className="text-xs text-tertiary truncate">{headline}</div>;
	} else if (companyName && jobTitle) {
		return (
			<div className="text-xs text-tertiary truncate">
				{jobTitle} at {companyName}
			</div>
		);
	} else if (companyName) {
		return <div className="text-xs text-tertiary truncate">{companyName}</div>;
	} else if (jobTitle) {
		return <div className="text-xs text-tertiary truncate">{jobTitle}</div>;
	} else {
		return <div></div>;
	}
}

export default function ReviewMasonryItem({
	rating,
	type,
	testimonial,
	date,
	source,
	tags,
	status,
	muxPlaybackId,
	videoThumbnailUrl,
	videoDuration,
	videoStatus: _videoStatus,
	isVideoProcessing,
	contentType,
	audioUrl,
	audioDuration,
	customerName,
	customerHeadline,
	customerJobTitle,
	customerAvatarUrl,
	companyName,
	companyLogoUrl,
	onDelete,
	onEdit,
	onApprove,
	onUnapprove,
	onClick,
	isDemoMode = false,
	isStatusChanging,
}: ReviewMasonryItemProps) {
	const isApproved = status === "approved";
	const isPending = status === "pending";
	const [videoModalOpen, setVideoModalOpen] = useState(false);
	const isAudio = contentType === "audio";
	const hasVideo = !isAudio && (!!muxPlaybackId || isVideoProcessing);
	const effectiveOnClick = isDemoMode ? undefined : onClick;
	const effectiveOnEdit = isDemoMode ? undefined : onEdit;
	const effectiveOnDelete = isDemoMode ? undefined : onDelete;
	const effectiveOnUnapprove = isDemoMode ? undefined : onUnapprove;

	return (
		<>
			<div
				className={`group bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow break-inside-avoid w-full ${effectiveOnClick ? "cursor-pointer" : ""}`}
				onClick={(e) => {
					// Prevent navigation when clicking action buttons or media controls
					if ((e.target as HTMLElement).closest("button, audio")) return;
					effectiveOnClick?.();
				}}
			>
				{/* Section 1: Testimonial - Stars, Title, Content */}
				<div className="p-4">
					<div className="flex items-center gap-2 mb-3">
						{rating > 0 && <StarRating rating={rating} />}
						<ClassificationBadge type={type} />
					</div>
					{testimonial && (
						<p
							className="text-sm text-secondary leading-relaxed line-clamp-6 whitespace-pre-line"
							dangerouslySetInnerHTML={{ __html: sanitizeReviewText(testimonial.length > 280 ? `${testimonial.slice(0, 280)}...` : testimonial) }}
						/>
					)}
				</div>

				{/* Audio Player (if audio review) */}
				{isAudio && (
					<div className="px-4 pb-4">
						{audioUrl ? (
							<AudioPlayer src={audioUrl} initialDuration={audioDuration} stopPropagation />
						) : (
							<div className="w-full h-24 rounded-lg bg-gray-100 flex items-center justify-center text-xs text-tertiary">No audio available</div>
						)}
					</div>
				)}

				{/* Video Thumbnail (if video exists) */}
				{hasVideo && (
					<button
						type="button"
						onClick={() => !isVideoProcessing && muxPlaybackId && setVideoModalOpen(true)}
						className={`block relative w-full aspect-video bg-gray-100 overflow-hidden ${isVideoProcessing ? "cursor-default" : "cursor-pointer"}`}
						disabled={isVideoProcessing}
					>
						{isVideoProcessing ? (
							/* Processing state */
							<div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex flex-col items-center justify-center">
								<svg className="w-10 h-10 text-brand animate-spin mb-2" fill="none" viewBox="0 0 24 24">
									<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
									<path
										className="opacity-75"
										fill="currentColor"
										d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
									/>
								</svg>
								<span className="text-sm text-tertiary">Processing video...</span>
							</div>
						) : videoThumbnailUrl ? (
							<img src={videoThumbnailUrl} alt="Video thumbnail" className="w-full h-full object-cover" />
						) : (
							<div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
								<svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={1.5}
										d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
									/>
								</svg>
							</div>
						)}
						{/* Play button overlay - only show when video is ready */}
						{!isVideoProcessing && (
							<div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
								<div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
									<svg className="w-6 h-6 text-gray-900 ml-1" fill="currentColor" viewBox="0 0 24 24">
										<path d="M8 5v14l11-7z" />
									</svg>
								</div>
							</div>
						)}
						{/* Duration badge */}
						{videoDuration && !isVideoProcessing && (
							<div className="absolute bottom-2 right-2 px-2 py-0.5 rounded bg-black/70 text-white text-xs font-medium">{formatDuration(videoDuration)}</div>
						)}
					</button>
				)}

				{/* Section 2: Author - Avatar, Name, Job Title */}
				<div className="p-4 border-t border-gray-100 bg-gray-50/50">
					<div className="flex items-center gap-3">
						{customerAvatarUrl ? (
							<img src={customerAvatarUrl} alt={customerName} className="w-9 h-9 rounded-full object-cover shrink-0" />
						) : (
							<div className="w-9 h-9 rounded-full bg-brand/10 flex items-center justify-center text-brand font-semibold text-sm shrink-0">
								{customerName?.charAt(0).toUpperCase() || "?"}
							</div>
						)}
						<div className="min-w-0">
							<div className="text-sm font-medium text-primary">{customerName || "Anonymous"}</div>
							<CustomerHeadline headline={customerHeadline} companyName={companyName} jobTitle={customerJobTitle} />
						</div>
					</div>
				</div>

				{/* Section 3: Company - Logo, Name */}
				{companyName && (
					<div className="p-4 border-t border-gray-100 flex items-center gap-2">
						{companyLogoUrl ? (
							<img src={companyLogoUrl} alt={companyName} className="w-5 h-5 rounded object-contain shrink-0" />
						) : (
							<div className="w-5 h-5 rounded bg-gray-100 flex items-center justify-center text-gray-500 font-medium text-xs shrink-0">{companyName.charAt(0).toUpperCase()}</div>
						)}
						<span className="text-xs font-medium text-tertiary">{companyName}</span>
					</div>
				)}

				{/* Section 4: Meta - Source, Date, Edit/Delete */}
				<div className="p-4 border-t border-gray-100 flex items-center justify-between">
					<div className="flex items-center gap-2">
						{source && <SourceBadge source={source} />}
						<span className="text-xs text-tertiary">{date}</span>
					</div>
					<div className="flex items-center gap-1">
						{effectiveOnEdit && (
							<button
								onClick={effectiveOnEdit}
								className="p-1 text-gray-400 hover:text-brand hover:bg-brand/10 rounded opacity-0 group-hover:opacity-100 transition-all cursor-pointer"
								aria-label="Edit review"
							>
								<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={1.5}
										d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
									/>
								</svg>
							</button>
						)}
						{effectiveOnDelete && (
							<button
								onClick={effectiveOnDelete}
								className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-all cursor-pointer"
								aria-label="Delete review"
							>
								<IconRecycleBinThrow className="w-4 h-4" />
							</button>
						)}
					</div>
				</div>

				{/* Section 5: Status & Approval Actions */}
				<div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
					<div className="flex items-center gap-2">
						{isStatusChanging && (
							<svg className="w-4 h-4 animate-spin text-gray-400" fill="none" viewBox="0 0 24 24">
								<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
								<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
							</svg>
						)}
						<StatusBadge status={status} />
					</div>
					<div className="flex items-center gap-2">
						{/* Show Approve button if not approved */}
						{!isApproved && onApprove && (
							<button
								onClick={onApprove}
								disabled={isStatusChanging}
								className="px-3 py-1.5 text-xs font-medium rounded cursor-pointer text-green-600 hover:text-green-700 hover:bg-green-50 border border-green-200 disabled:opacity-50 disabled:cursor-not-allowed"
								aria-label="Approve review"
							>
								Approve
							</button>
						)}
						{/* Show Unapprove button if approved OR pending */}
						{(isApproved || isPending) && effectiveOnUnapprove && (
							<button
								onClick={effectiveOnUnapprove}
								disabled={isStatusChanging}
								className="px-3 py-1.5 text-xs font-medium rounded cursor-pointer text-amber-600 hover:text-amber-700 hover:bg-amber-50 border border-amber-200 disabled:opacity-50 disabled:cursor-not-allowed"
								aria-label="Unapprove review"
							>
								Unapprove
							</button>
						)}
					</div>
				</div>

				{/* Section 6: Tags */}
				{tags && tags.length > 0 && (
					<div className="p-4 border-t border-gray-100 flex flex-wrap items-center gap-1.5">
						{tags.slice(0, 3).map((tag) => {
							const colorStyles = getTagColorStyles(tag.color);
							return (
								<span key={tag.id} className={`inline-flex items-center px-2 py-1 rounded text-xs ${colorStyles.bg} ${colorStyles.text} ${colorStyles.border || ""}`}>
									{tag.label}
								</span>
							);
						})}
						{tags.length > 3 && <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-gray-100 text-gray-500 border border-gray-200">+{tags.length - 3}</span>}
					</div>
				)}
			</div>

			{/* Video Modal */}
			{muxPlaybackId && <VideoModal open={videoModalOpen} onOpenChange={setVideoModalOpen} playbackId={muxPlaybackId} />}
		</>
	);
}
